import { QuestionCircleOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import i18n from 'i18next';
import { flatten, get, startCase } from 'lodash';
import moment from 'moment-timezone';

import {
  getColumnName,
  getSiteColumnCopy,
  renderAssetNameWithAncestors,
  renderAssetSite,
} from 'components/data/assets/assetColumnRenderers';
import { generateDateColumn } from 'components/data/assets/columnHelpers';
import { getFilter, getFilterDropdown, getOnFilter, None, sortBy } from 'components/data/helpers';
import { type TEditColumnData } from 'components/data/types';
import {
  AssetFilter,
  assetFilterColumnProps,
  DamageIdAndStatusFilter,
  DefaultStateToGraphqlKeyMap,
  MAKE_MODEL_CLIENT_ONLY_FILTER_KEYS,
  SiteFilter,
  TasksFilter,
} from 'components/DataTable/customFilters';
import { CheckIcon } from 'components/Icons';
import { StatusTag } from 'components/StatusTag';
import { TooltipWithQuestionIcon } from 'components/ui';
import {
  InspectionsPopoverTable,
  ObservationsPopoverTable,
  TasksPopoverTable,
} from 'horizon/components/Damages/Popovers';
import {
  getPillForecast,
  getPredictedPropagationSummaryForDamage,
  PredictedPropagationTag,
  PROPAGATION_SORT_KEY,
} from 'horizon/components/Damages/PredictedPropagation';
import { PropagationTag } from 'horizon/components/Damages/PropagationTag';
import {
  DamageLink,
  getPrimaryObservationGroupForDamage,
  propagationTypeMapping,
  resolveGroupPropagation,
} from 'horizon/components/Damages/utils';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlAsset,
  type AtlasGqlAssetType,
  type AtlasGqlHorizonDamage,
  type AtlasGqlHorizonDamageObservation,
  type AtlasGqlHorizonDamageObservationGroup,
  type AtlasGqlInspection,
  type AtlasGqlTask,
  AtlasGqlConfirmationStatus,
} from 'types/atlas-graphql';
import naturalSort from 'utils/naturalSort';

import { DamageNumberContainer, HelpTooltip } from './DamageTable2.style';

const getSiteSortValue = (record: AtlasGqlHorizonDamage) =>
  record.asset ? getSiteColumnCopy(record.asset) : '';
const getAssetSortValue = (record: AtlasGqlHorizonDamage) =>
  getColumnName(record.asset ?? undefined);

export const createDamageIdColumn = (
  t: (key: string) => string
): TTableColumnDef<AtlasGqlHorizonDamage> => ({
  key: 'id',
  allKeys: ['confirmationStatus'],
  title: (
    <span>
      {t('table.damageId', 'Damage ID')}
      <HelpTooltip
        title={i18n.t(
          'damage.damageIdTooltip',
          'A Damage is a group of one or more Observations. This Damage ID refers to the group. Individual images Observation IDs can be found by hovering over their red bounding box.'
        )}
        placement="right"
      >
        <QuestionCircleOutlined />
      </HelpTooltip>
    </span>
  ),
  dataIndex: 'id',
  filterProperty: 'id',
  filterDropdown: props => <DamageIdAndStatusFilter {...props} />,
  defaultFilterOperator: 'CONTAINS',
  render: (_, record) => (
    <DamageNumberContainer>
      <DamageLink damageId={record.id} />
      {record.confirmationStatus === AtlasGqlConfirmationStatus.Confirmed && <CheckIcon />}
    </DamageNumberContainer>
  ),
});

// Backward compatibility
export const damageIdColumn: TTableColumnDef<AtlasGqlHorizonDamage> = {
  key: 'id',
  allKeys: ['confirmationStatus'],
  title: (
    <span>
      {i18n.t('table.damageId', 'Damage ID')}
      <HelpTooltip
        title={i18n.t(
          'damage.damageIdTooltip',
          'A Damage is a group of one or more Observations. This Damage ID refers to the group. Individual images Observation IDs can be found by hovering over their red bounding box.'
        )}
        placement="right"
      >
        <QuestionCircleOutlined />
      </HelpTooltip>
    </span>
  ),
  dataIndex: 'id',
  filterProperty: 'id',
  filterDropdown: props => <DamageIdAndStatusFilter {...props} />,
  defaultFilterOperator: 'CONTAINS',
  render: (_, record) => (
    <DamageNumberContainer>
      <DamageLink damageId={record.id} />
      {record.confirmationStatus === AtlasGqlConfirmationStatus.Confirmed && <CheckIcon />}
    </DamageNumberContainer>
  ),
};

export const createTasksColumn = (
  t: (key: string) => string
): TTableColumnDef<AtlasGqlHorizonDamage> => ({
  key: 'tasks',
  allKeys: ['taskCount', 'taskType', 'taskStatus', 'hasUnarchivedTasks', 'hasPrimaryGroupTasks'],
  title: t('workOrder.task', 'Tasks'),
  dataIndex: 'tasks',
  filterDropdown: props => <TasksFilter {...props} />,
  sortKey: 'taskCount',
  sorter: sortBy('taskCount'),
  render: (tasks: AtlasGqlTask[]) => <TasksPopoverTable tasks={tasks} />,
});

// Backward compatibility
export const tasksColumn: TTableColumnDef<AtlasGqlHorizonDamage> = {
  key: 'tasks',
  allKeys: ['taskCount', 'taskType', 'taskStatus', 'hasUnarchivedTasks', 'hasPrimaryGroupTasks'],
  title: i18n.t('workOrder.task', 'Tasks'),
  dataIndex: 'tasks',
  filterDropdown: props => <TasksFilter {...props} />,
  sortKey: 'taskCount',
  sorter: sortBy('taskCount'),
  get: record => get(record, 'taskCount'),
  render: (tasks: AtlasGqlTask[]) => <TasksPopoverTable tasks={tasks} />,
};

export const createSiteColumn = (
  t: (key: string) => string
): TTableColumnDef<AtlasGqlHorizonDamage> => ({
  key: 'site',
  title: t('table.site', 'Site'),
  dataIndex: 'asset',
  filterProperty: 'asset.site',
  filterDropdown: props => <SiteFilter {...props} />,
  sortKey: 'site',
  sorter: sortBy('asset.site'),
  render: renderAssetSite,
});

// Backward compatibility
export const siteColumn: TTableColumnDef<AtlasGqlHorizonDamage> = {
  key: 'site',
  title: i18n.t('table.site', 'Site'),
  dataIndex: 'asset',
  filterProperty: 'asset.site',
  filterDropdown: props => <SiteFilter {...props} />,
  sortKey: 'site',
  sorter: (a, b) => naturalSort(getSiteSortValue(a), getSiteSortValue(b)),
  get: getSiteSortValue,
  render: renderAssetSite,
};

export const getSiteColumn = (
  lockedSiteIds?: string[]
): TTableColumnDef<AtlasGqlHorizonDamage> => ({
  ...siteColumn,
  filtered: !!lockedSiteIds?.length || undefined,
  filterDropdown: props => <SiteFilter {...props} lockedSiteIds={lockedSiteIds} />,
});

export const createAssetColumn = (
  t: (key: string) => string,
  tableId: string,
  lockedAssetTypes?: AtlasGqlAssetType[],
  lockedAssetIds?: string[]
): TTableColumnDef<AtlasGqlHorizonDamage> => ({
  key: 'asset',
  allKeys: [...Object.values(DefaultStateToGraphqlKeyMap), ...MAKE_MODEL_CLIENT_ONLY_FILTER_KEYS],
  title: t('table.asset', 'Asset'),
  dataIndex: 'asset',
  filterProperty: 'asset',
  filtered: !!lockedAssetIds?.length || !!lockedAssetTypes?.length || undefined,
  ...assetFilterColumnProps,
  filterDropdown: props => (
    <AssetFilter
      {...props}
      tableId={tableId}
      lockedAssetTypes={lockedAssetTypes}
      lockedAssetIds={lockedAssetIds}
      enableNestedMakeModelFilter
    />
  ),
  serverSideFilterParsers: {
    assetId: (v: string) => v.split(':')[0],
    componentId: (v: string) => v.split(':')[0],
  },
  sortKey: 'asset',
  sorter: sortBy('asset'),
  render: (asset: AtlasGqlAsset) => renderAssetNameWithAncestors(asset, 'damages2'),
});

// Backward compatibility
export const getAssetColumn = (
  tableId: string,
  lockedAssetTypes?: AtlasGqlAssetType[],
  lockedAssetIds?: string[]
): TTableColumnDef<AtlasGqlHorizonDamage> => ({
  key: 'asset',
  allKeys: [...Object.values(DefaultStateToGraphqlKeyMap), ...MAKE_MODEL_CLIENT_ONLY_FILTER_KEYS],
  title: i18n.t('table.asset', 'Asset'),
  dataIndex: 'asset',
  filterProperty: 'asset',
  filtered: !!lockedAssetIds?.length || !!lockedAssetTypes?.length || undefined,
  ...assetFilterColumnProps,
  filterDropdown: props => (
    <AssetFilter
      {...props}
      tableId={tableId}
      lockedAssetTypes={lockedAssetTypes}
      lockedAssetIds={lockedAssetIds}
      enableNestedMakeModelFilter
    />
  ),
  serverSideFilterParsers: {
    assetId: (v: string) => v.split(':')[0],
    componentId: (v: string) => v.split(':')[0],
  },
  sortKey: 'asset',
  sorter: (a, b) => naturalSort(getAssetSortValue(a), getAssetSortValue(b)),
  get: getAssetSortValue,
  onHeaderCell: () =>
    ({
      'data-testid': 'damage-asset-header',
    }) as any,
  render: (asset: AtlasGqlAsset) => renderAssetNameWithAncestors(asset, 'damages2'),
});

export const assetColumn: TTableColumnDef<AtlasGqlHorizonDamage> = {
  key: 'asset',
  title: i18n.t('table.asset', 'Asset'),
  dataIndex: 'asset',
  render: (asset: AtlasGqlAsset) => renderAssetNameWithAncestors(asset, 'damages2'),
};

export const assetTypeColumn: TTableColumnDef<AtlasGqlHorizonDamage> = {
  key: 'assetType',
  title: i18n.t('table.assetType', 'Asset Type'),
  dataIndex: 'asset',
  render: (asset: AtlasGqlAsset) => asset?.assetType?.name ?? <None />,
};

export const confirmationStatusColumn: TEditColumnData = {
  key: 'confirmationStatus',
  title: i18n.t('table.confirmationStatus', 'Confirmation Status'),
  dataIndex: 'confirmationStatus',
  filterProperty: 'confirmationStatus',
  className: 'hidden-column',
  render: (confirmationStatus: string) => confirmationStatus ?? <None />,
  edit(
    record: any,
    {
      onChange: _onChange,
      loading: _loading,
      ...props
    }: { onChange: (data: any) => void; loading: boolean }
  ) {
    const { confirmationStatus, editable } = record || {};
    const options = [AtlasGqlConfirmationStatus.Confirmed, AtlasGqlConfirmationStatus.NotConfirmed];

    return (
      <Select
        value={confirmationStatus}
        {...props}
        disabled={!editable}
        options={options.map(value => ({ value, label: startCase(value) }))}
      />
    );
  },
  editRule: 'DAMAGE_CONFIRM',
};

export const criticalColumn: TEditColumnData = {
  key: 'critical',
  title: i18n.t('table.critical', 'Critical'),
  dataIndex: 'critical',
  filterProperty: 'critical',
  className: 'hidden-column',
  render: (critical: string) => critical ?? <None />,
  edit(
    record: any,
    {
      onChange: _onChange,
      loading: _loading,
      ...props
    }: { onChange: (data: any) => void; loading: boolean }
  ) {
    const { critical, editable } = record || {};
    const options = [
      { key: 'True', value: true },
      { key: 'False', value: false },
    ];

    return (
      <Select
        value={critical}
        {...props}
        disabled={!editable}
        options={options.map(({ key, value }) => ({ value, label: startCase(key) }))}
      />
    );
  },
  editRule: 'DAMAGE_EDIT_ATTRIBUTES',
};

export const getAssetStatusColumn = (
  filterOptions: string[]
): TTableColumnDef<AtlasGqlHorizonDamage> => {
  const assetStatusConfig = {
    key: 'assetStatus',
    type: 'string',
    filterAttr: 'assetStatus',
    exactMatch: false,
    values: [
      ...filterOptions.map(v => ({
        id: v,
        label: v,
      })),
    ],
    includeNoneOption: true,
    noneOptionValue: 'IS_NULL',
  };

  return {
    key: 'assetStatus',
    title: i18n.t('table.assetStatus', 'Asset Status'),
    dataIndex: 'asset',
    filterProperty: 'asset.assetStatus',
    filters: getFilter(assetStatusConfig),
    filterDropdown: getFilterDropdown(assetStatusConfig),
    onFilter: getOnFilter(assetStatusConfig),
    sortKey: 'assetStatus',
    sorter: sortBy('asset.assetStatus'),
    get: record => record?.asset?.assetStatus,
    render: (asset: AtlasGqlAsset) =>
      asset?.assetType?.name &&
      !['Site', 'Turbine'].includes(asset.assetType.name) &&
      asset?.assetStatus ? (
        <StatusTag value={asset.assetStatus} />
      ) : (
        <None />
      ),
  };
};

// TODO: deprecate -> replaced by getAssetStatusColumn
export const assetStatusColumn: TTableColumnDef<AtlasGqlHorizonDamage> = {
  key: 'assetStatus',
  title: i18n.t('table.assetStatus', 'Asset Status'),
  dataIndex: 'asset',
  render: (asset: AtlasGqlAsset) =>
    asset?.assetType?.name &&
    !['Site', 'Turbine'].includes(asset.assetType.name) &&
    asset?.assetStatus ? (
      <StatusTag value={asset.assetStatus} />
    ) : (
      <None />
    ),
};

// TODO: deprecate -> replaced by getInspectionsColumn
export const inspectionsColumn: TTableColumnDef<AtlasGqlHorizonDamage> = {
  key: 'inspections',
  title: i18n.t('table.inspections', 'Inspections'),
  dataIndex: 'observationGroups',
  render: (observationGroups: AtlasGqlHorizonDamageObservationGroup[]) => {
    const inspections = observationGroups
      .map(og => og?.observations?.[0]?.inspection)
      .filter((i): i is AtlasGqlInspection => !!i);

    return <InspectionsPopoverTable inspections={inspections} />;
  },
};

export const getInspectionsColumn = (): TTableColumnDef<AtlasGqlHorizonDamage> => {
  const inspectionCountConfig = {
    key: 'inspectionCount',
    type: 'number',
    filterAttr: 'inspectionCount',
    exactMatch: false,
    values: [
      { id: 'min', label: 1 },
      { id: 'max', label: Infinity },
    ],
    roundValue: true,
    step: 1,
    includeSlider: false,
    includeNoneOption: false,
  };

  return {
    key: 'inspectionCount',
    title: i18n.t('table.inspections', 'Inspections'),
    dataIndex: 'observationGroups',
    filterProperty: 'inspectionCount',
    filters: getFilter(inspectionCountConfig),
    filterDropdown: getFilterDropdown(inspectionCountConfig),
    onFilter: getOnFilter(inspectionCountConfig),
    sortKey: 'inspectionCount',
    sorter: sortBy('inspectionCount'),
    get: record => get(record, 'inspectionCount'),
    render: (observationGroups: AtlasGqlHorizonDamageObservationGroup[]) => {
      const inspections = observationGroups
        .map(og => og?.observations?.[0]?.inspection)
        .filter((i): i is AtlasGqlInspection => !!i);

      return <InspectionsPopoverTable inspections={inspections} />;
    },
  };
};

// TODO: deprecate -> replaced by getObservationsColumn
export const observationsColumn: TTableColumnDef<AtlasGqlHorizonDamage> = {
  key: 'observations',
  title: i18n.t('table.observations', 'Observations'),
  dataIndex: 'observationGroups',
  render: (observationGroups: AtlasGqlHorizonDamageObservationGroup[]) => {
    const observations = flatten(observationGroups.map(og => og.observations)).filter(
      (o): o is AtlasGqlHorizonDamageObservation => !!o
    );

    return <ObservationsPopoverTable observations={observations} />;
  },
};

export const getObservationsColumn = (): TTableColumnDef<AtlasGqlHorizonDamage> => {
  const observationCountConfig = {
    key: 'observationCount',
    type: 'number',
    filterAttr: 'observationCount',
    exactMatch: false,
    values: [
      { id: 'min', label: 1 },
      { id: 'max', label: Infinity },
    ],
    roundValue: true,
    step: 1,
    includeSlider: false,
    includeNoneOption: false,
  };

  return {
    key: 'observationCount',
    title: i18n.t('table.observations', 'Observations'),
    dataIndex: 'observationGroups',
    filterProperty: 'observationCount',
    filters: getFilter(observationCountConfig),
    filterDropdown: getFilterDropdown(observationCountConfig),
    onFilter: getOnFilter(observationCountConfig),
    sortKey: 'observationCount',
    sorter: sortBy('observationCount'),
    get: record => get(record, 'observationCount'),
    render: (observationGroups: AtlasGqlHorizonDamageObservationGroup[]) => {
      const observations = flatten(observationGroups.map(og => og.observations)).filter(
        (o): o is AtlasGqlHorizonDamageObservation => !!o
      );

      return <ObservationsPopoverTable observations={observations} />;
    },
  };
};

export const getPropagationsColumn = (): TTableColumnDef<AtlasGqlHorizonDamage> => {
  const propagationTypeConfig = {
    key: 'propagationType',
    type: 'string',
    filterAttr: 'propagationType',
    exactMatch: false,
    values: [
      ...Object.keys(propagationTypeMapping).map(key => ({
        id: key,
        label: propagationTypeMapping[key],
      })),
      { id: '', label: <i>{i18n.t('common.none', 'none')}</i> },
    ],
    includeNoneOption: true,
  };

  return {
    key: 'propagationType',
    title: i18n.t('table.propagationType', 'Propagation Type'),
    dataIndex: 'propagations',
    filterProperty: 'propagationType',
    filters: getFilter(propagationTypeConfig),
    filterDropdown: getFilterDropdown(propagationTypeConfig),
    onFilter: getOnFilter(propagationTypeConfig),
    // sorter: sortBy('propagationType'),
    render: (_, record) => {
      const primaryGroup = getPrimaryObservationGroupForDamage(record);
      const propagation = resolveGroupPropagation(
        record.propagations ?? [],
        record.observationGroups ?? [],
        primaryGroup
      );

      return propagation?.type ? <PropagationTag propagation={propagation} /> : <None />;
    },
  };
};

// Sorted server-side on the 12-month growth chance the tag reports.
// TODO: add a filter. The backend supports filtering on the predicted propagation.
export const getFuturePropagationColumn = (): TTableColumnDef<AtlasGqlHorizonDamage> => ({
  key: PROPAGATION_SORT_KEY,
  title: i18n.t('table.futurePropagation', 'Future Propagation'),
  dataIndex: 'observationGroups',
  sortKey: PROPAGATION_SORT_KEY,
  sorter: true,
  render: (_, record) => {
    const summary = getPredictedPropagationSummaryForDamage(record);
    // The tag renders nothing for a 0% chance, so ask first to show None instead.
    return getPillForecast(summary) ? <PredictedPropagationTag summary={summary} /> : <None />;
  },
});

export const CREATED_DATE_RANGE_LIMIT = 7;
export const getPrimaryGroupSourceObservationCreatedColumn = (
  shouldLimitRangeSelection: boolean
): TTableColumnDef<AtlasGqlHorizonDamage> => {
  const column = generateDateColumn(
    'primaryGroupSourceObservationCreated',
    i18n.t('table.createdDate', 'Created Date'),
    'primaryGroupSourceObservationCreated',
    true,
    shouldLimitRangeSelection ? CREATED_DATE_RANGE_LIMIT : undefined
  );

  return {
    ...column,
    title: (
      <span>
        {column.title}{' '}
        <TooltipWithQuestionIcon
          message={
            <>
              <span>
                {i18n.t(
                  'damage.createdDatePrimaryTooltip',
                  'The created date of the primary observation in the damage - in most cases, this is the most recent observation.'
                )}
              </span>
              <br />
              <br />
              <span>
                {i18n.t(
                  'damage.createdDateSortTooltip',
                  'Sorting by this column will cause a filter for the last {{limit}} days to be applied. This filter may be adjusted, but must be a window of at most {{limit}} days.',
                  { limit: CREATED_DATE_RANGE_LIMIT }
                )}
              </span>
            </>
          }
          iconStyle={{ marginRight: 0 }}
        />
      </span>
    ),
    render: date => (date ? moment(date).tz(moment.tz.guess()).format('YYYY-MM-DD') : <None />),
  };
};
